/**
 * WebSocket Service for Realtime Updates
 * Hanterar WebSocket-anslutning till backend för realtids-positioner
 */

import { io, Socket } from 'socket.io-client';

export interface PositionUpdate {
  imei: string;
  vehicleId?: string;
  registrationNumber?: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  speed: number;
  heading: number;
  timestamp: number;
  ignition?: number;
  movement?: number;
}

export interface TripCompletedEvent {
  tripId: string;
  imei: string;
  vehicleId: number;
  registrationNumber?: string;
  startTime: string;
  endTime: string;
  distance: number;
  duration: number;
  maxSpeed: number;
  avgSpeed: number;
  startAddress?: string;
  endAddress?: string;
}

type PositionCallback = (position: PositionUpdate) => void;
type TripCompletedCallback = (trip: TripCompletedEvent) => void;
type ConnectionCallback = (connected: boolean) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private positionCallbacks: Set<PositionCallback> = new Set();
  private tripCompletedCallbacks: Set<TripCompletedCallback> = new Set();
  private connectionCallbacks: Set<ConnectionCallback> = new Set();
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;

  /**
   * Anslut till WebSocket-servern
   */
  connect(): void {
    if (this.socket?.connected) return;

    const baseUrl = (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1')
  ? window.location.origin
  : (import.meta.env.VITE_API_BASE_URL || window.location.origin);

    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.notifyConnectionCallbacks(true);
    });

    this.socket.on('disconnect', () => {
      this.notifyConnectionCallbacks(false);
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
    });

    // Lyssna på positions-uppdateringar
    this.socket.on('position:update', (position: PositionUpdate) => {
      this.notifyPositionCallbacks(position);
    });

    // Lyssna på batch-uppdateringar
    this.socket.on('positions:batch', (positions: PositionUpdate[]) => {
      positions.forEach((position) => {
        this.notifyPositionCallbacks(position);
      });
    });

    this.socket.on('connected', () => {});

    // Lyssna på trip:completed events
    this.socket.on('trip:completed', (trip: TripCompletedEvent) => {
      this.notifyTripCompletedCallbacks(trip);
    });
  }

  /**
   * Koppla från WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Kontrollera om ansluten
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Prenumerera på positions-uppdateringar
   */
  onPositionUpdate(callback: PositionCallback): () => void {
    this.positionCallbacks.add(callback);
    return () => {
      this.positionCallbacks.delete(callback);
    };
  }

  /**
   * Prenumerera på anslutnings-status
   */
  onConnectionChange(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.add(callback);
    return () => {
      this.connectionCallbacks.delete(callback);
    };
  }

  /**
   * Prenumerera på trip:completed events
   * Anropas när en tripp har avslutats på servern
   */
  onTripCompleted(callback: TripCompletedCallback): () => void {
    this.tripCompletedCallbacks.add(callback);
    return () => {
      this.tripCompletedCallbacks.delete(callback);
    };
  }

  /**
   * Prenumerera på specifikt fordon
   */
  subscribeToVehicle(vehicleId: string): void {
    this.socket?.emit('subscribe:vehicle', vehicleId);
  }

  /**
   * Avprenumerera från fordon
   */
  unsubscribeFromVehicle(vehicleId: string): void {
    this.socket?.emit('unsubscribe:vehicle', vehicleId);
  }

  private notifyPositionCallbacks(position: PositionUpdate): void {
    this.positionCallbacks.forEach((callback) => {
      try {
        callback(position);
      } catch (error) {
        console.error('Error in position callback:', error);
      }
    });
  }

  private notifyConnectionCallbacks(connected: boolean): void {
    this.connectionCallbacks.forEach((callback) => {
      try {
        callback(connected);
      } catch (error) {
        console.error('Error in connection callback:', error);
      }
    });
  }

  private notifyTripCompletedCallbacks(trip: TripCompletedEvent): void {
    this.tripCompletedCallbacks.forEach((callback) => {
      try {
        callback(trip);
      } catch (error) {
        console.error('Error in trip completed callback:', error);
      }
    });
  }
}

// Singleton-export
export const websocketService = new WebSocketService();

